var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { Alert, Checkmark, Close, InfoWithoutCircle, } from '@volkswagen-onehub/icons-core';
import { CSSTransition } from 'react-transition-group';
import { styled, css, ThemeProvider } from '../theme-provider';
import { Text, TextTag, TokenTextColor, TokenTextAppearance } from '../text';
import { Breakpoints } from '../helpers/breakpoint';
const getColorForOption = (theme, appearance) => {
    if (appearance === 'Success') {
        return theme.colors.signal.success;
    }
    if (appearance === 'Alert') {
        return theme.colors.signal.error;
    }
    if (appearance === 'Informative') {
        return theme.colors.signal.neutral;
    }
    return theme.colors.content.secondary;
};
const StyledIconWrapper = styled.div.withConfig({ displayName: "StyledIconWrapper", componentId: "sc-d737za" }) `border-radius:50%;height:${({ theme }) => theme.size.static350};width:${({ theme }) => theme.size.static350};display:flex;flex:none;align-items:center;justify-content:center;`;
// This function is used to get the styles for the 2 variants of the ToastNotification
const getColorStylesForVariant = (variant, appearance) => variant === 'toast signal'
    ? css `background:${({ theme }) => getColorForOption(theme, appearance)};color:${({ theme }) => theme.colors.content.inverted};${StyledIconWrapper}{background:${({ theme }) => theme.colors.content.inverted};> svg{flex:none;fill:${({ theme }) => getColorForOption(theme, appearance)};}}`
    : css `color:${({ theme }) => theme.colors.content.primary};background:${({ theme }) => theme.colors.content.inverted};${StyledIconWrapper}{background:${({ theme }) => getColorForOption(theme, appearance)};svg{flex:none;fill:${({ theme }) => theme.colors.content.inverted};}}`;
const animationClassName = 'animation';
const transition = css `transition:${({ theme }) => {
    const duration = theme.animation.duration.layer;
    const timingFunction = theme.animation.timingFunction.in;
    return `opacity ${duration} ease, transform ${duration} ${timingFunction}`;
}};`;
const toastAnimation = (fadeIn) => css `top:${({ theme }) => theme.size.static250};opacity:1;&.${animationClassName}-enter{opacity:0;transform:${({ theme }) => {
    if (fadeIn)
        return 'translateY(0)';
    return `translateX(${theme.direction === 'rtl' ? '-500px' : '500px'})`;
}};}&.${animationClassName}-enter-done{opacity:1;transform:translateX(0);${transition};}&.${animationClassName}-exit{opacity:1;transform:translateX(0);}&.${animationClassName}-exit-active{opacity:0;transform:translateY(-100%);${transition};}@media (max-width: ${Breakpoints.b560}px){margin:0 auto;bottom:${({ theme }) => theme.size.static250};top:unset;left:${({ theme }) => theme.size.static200};right:${({ theme }) => theme.size.static200};&.${animationClassName}-enter{opacity:0;transform:${fadeIn ? 'unset' : 'translateY(500px)'};}&.${animationClassName}-enter-active{opacity:1;transform:translateY(0);${transition};}&.${animationClassName}-exit-active,&.${animationClassName}-exit-done,&.${animationClassName}-exit{opacity:0;transform:unset;${transition};}}`;
const StyledToastWrapper = styled.div.withConfig({ displayName: "StyledToastWrapper", componentId: "sc-13f5kqc" }) `position:fixed;${({ theme }) => theme.direction === 'ltr'
    ? `right: ${theme.size.static250}`
    : `left: ${theme.size.static250}`};display:flex;flex-direction:${({ theme }) => theme.direction === 'rtl' && 'unset'};gap:${({ theme }) => theme.size.static200};min-width:264px;max-width:500px;height:fit-content;padding:${({ theme }) => theme.size.static200};padding-right:${({ theme }) => theme.size.static150};border-radius:${({ theme }) => theme.size.static100};box-shadow:${({ theme }) => theme.colors.shadow.variant3};${({ variant, appearance }) => variant && getColorStylesForVariant(variant, appearance)};div{display:flex;flex-direction:column;}div a,div button{margin-top:${({ theme }) => theme.size.static150};width:fit-content;}${({ fadeIn }) => toastAnimation(fadeIn)}`;
const StyledCloseIconWrapper = styled.button.withConfig({ displayName: "StyledCloseIconWrapper", componentId: "sc-1m8optf" }) `padding:0;border:0;background:none;box-shadow:none;cursor:pointer;align-self:start;${({ theme }) => theme.direction === 'ltr'
    ? css `
					margin-left: auto;
				`
    : css `
					margin-right: auto;
				`} svg{${({ variant, theme }) => variant === 'toast signal' &&
    css `
				fill: ${theme.colors.content.inverted};
			`};}`;
const getIconForOption = (appearance) => {
    if (appearance === 'Success') {
        return React.createElement(Checkmark, { variant: "small", "aria-hidden": true });
    }
    if (appearance === 'Alert') {
        return React.createElement(Alert, { variant: "small", "aria-hidden": true });
    }
    return React.createElement(InfoWithoutCircle, { "aria-hidden": true });
};
export const ToastNotification = (_a) => {
    var _b;
    var { variant = 'toast signal', title, content, appearance = 'Neutral', icon, action, show, onExited, onClose, fadeIn, uniqueAriaId, role = 'alert' } = _a, rest = __rest(_a, ["variant", "title", "content", "appearance", "icon", "action", "show", "onExited", "onClose", "fadeIn", "uniqueAriaId", "role"]);
    const handleClose = (e) => {
        if (onClose)
            onClose(e);
    };
    const handleKeyDownClose = (e) => {
        if (onClose && (e.key === ' ' || e.key === 'Escape')) {
            onClose(e);
        }
    };
    return (React.createElement(ThemeProvider, { theme: "main" },
        React.createElement(CSSTransition, { in: show, timeout: {
                enter: fadeIn ? 10 : 100,
                exit: 100,
            }, classNames: animationClassName, unmountOnExit: true, onExited: onExited },
            React.createElement(StyledToastWrapper, Object.assign({ role: role, uniqueAriaId: uniqueAriaId, "aria-labelledby": uniqueAriaId, variant: variant, appearance: appearance, fadeIn: fadeIn }, rest),
                React.createElement(StyledIconWrapper, null, icon !== null && icon !== void 0 ? icon : getIconForOption(appearance)),
                React.createElement("div", null,
                    typeof title === 'string' ? (React.createElement(Text, { tag: TextTag.p, bold: true, appearance: TokenTextAppearance.copy200, color: TokenTextColor.inherit }, title)) : (title),
                    typeof content === 'string' ? (React.createElement(Text, { tag: TextTag.p, color: TokenTextColor.inherit }, content)) : (content),
                    action && (React.createElement(ThemeProvider, { theme: variant === 'toast signal' ? 'inverted' : 'main' }, React.cloneElement(action, {
                        size: 'small',
                        emphasis: (_b = action.props.emphasis) !== null && _b !== void 0 ? _b : 'secondary',
                    })))),
                React.createElement(StyledCloseIconWrapper, { onClick: handleClose, onKeyDown: handleKeyDownClose, "aria-label": "Close notification", variant: variant },
                    React.createElement(Close, { "aria-hidden": true }))))));
};
ToastNotification.displayName = 'ToastNotification';
